import StickyButton from '@/components/atom/buttons/StickyButton';
import HealthMedicalMedicines from '@/components/atom/icon/layout/HealthMedicalMedicines';
import TemplateLayout from '@/components/layouts/TemplateLayout';
import StickyAppHeader from '@/components/molecules/sticky-app-header';
import AppInstallTopBanner from '@/components/templates/mobile/communication/common/AppInstallTopBanner';
import useAppInstallTopBanner from '@/hooks/cuj/useAppInstallTopBanner';
import { Client, ClientType } from '@/types/app';
import { HeaderType } from '@/types/header';
import { historyBack } from '@/utils/mobile/RNPlugins';
import DiseaseEncyclopediaBanner from '@/widgets/disease-encyclopedia-detail/ui/DiseaseEncyclopediaBanner';
import DiseaseEncyclopediaFaq from '@/widgets/disease-encyclopedia-detail/ui/DiseaseEncyclopediaFaq';
import DiseaseEncyclopediaGuideInfo from '@/widgets/disease-encyclopedia-detail/ui/DiseaseEncyclopediaGuideInfo';
import DiseaseEncyclopediaHeader from '@/widgets/disease-encyclopedia-detail/ui/DiseaseEncyclopediaHeader';
import DiseaseEncyclopediaMarkDown from '@/widgets/disease-encyclopedia-detail/ui/DiseaseEncyclopediaMarkDown';
import DiseaseEncyclopediaSearchTagList from '@/widgets/disease-encyclopedia-detail/ui/DiseaseEncyclopediaSearchTagList';
import DiseaseEncyclopediaTableOfContents from '@/widgets/disease-encyclopedia-detail/ui/DiseaseEncyclopediaTableOfContents';
import { Button } from 'doctornow-design-system';
import React from 'react';
import styled from 'styled-components';
import { DiseaseEncyclopediaDetailViewModel } from '../hooks/useDiseaseEncyclopediaDetailViewModel';

interface Props {
  viewModel: DiseaseEncyclopediaDetailViewModel;
  topBannerViewModel: ReturnType<typeof useAppInstallTopBanner>;
  client: Client;
}

const DiseaseEncyclopediaDetailView = ({ viewModel, topBannerViewModel, client }: Props) => (
  <TemplateLayout.Wrapper>
    {client.isWebView && (
      <TemplateLayout.StickyHeader type={HeaderType.WithMobileHeader}>
        <StickyAppHeader title={viewModel.getTitle()} hasLeftBackButton={true} onClickBackButton={historyBack} />
      </TemplateLayout.StickyHeader>
    )}
    {client.clientType === ClientType.MOBILE_WEB && topBannerViewModel.isVisible && (
      <TemplateLayout.StickyHeader
        style={{
          top: 60,
        }}
      >
        <AppInstallTopBanner
          onLogEvent={viewModel.onClickTopCta}
          onClickClose={topBannerViewModel.hideBanner}
          content='web_content_bnr_service'
          trackingInfo={{
            campaign: 'web_disease_encyclopedia',
            contentId: viewModel.diseaseEncyclopedia?.getPublicId(),
            contentTitle: viewModel.diseaseEncyclopedia?.getTitle(),
          }}
        />
      </TemplateLayout.StickyHeader>
    )}

    <main className='flex-1'>
      <DiseaseEncyclopediaHeader
        client={client}
        shouldShowDiseaseImage={viewModel.shouldShowDiseaseImage()}
        shouldShowDepartmentTags={viewModel.shouldShowDepartmentTags()}
        shouldShowSymptomTags={viewModel.shouldShowSymptomTags()}
        title={viewModel.getTitle()}
        diseaseEncyclopedia={viewModel.diseaseEncyclopedia}
        joinedDepartmentTags={viewModel.diseaseEncyclopedia?.getJoinedDepartmentTags() ?? ''}
        joinedSymptomTags={viewModel.diseaseEncyclopedia?.getJoinedSymptomTags() ?? ''}
      />

      {viewModel.shouldShowTableOfContents() && (
        <DiseaseEncyclopediaTableOfContents
          content={viewModel.getContent()}
          hasFaq={viewModel.showShowFaqs()}
          faqTitle={viewModel.getFaqTitle()}
        />
      )}

      {viewModel.shouldShowContent() && (
        <DiseaseEncyclopediaMarkDown
          content={viewModel.getContent()}
          onClickOuterLink={viewModel.onClickOuterLink}
          extractTextFromChildren={viewModel.extractTextFromChildren}
          encyclopediaPid={viewModel.diseaseEncyclopedia?.getPublicId() ?? ''}
        />
      )}

      {viewModel.showShowFaqs() && (
        <DiseaseEncyclopediaFaq faqTitle={viewModel.getFaqTitle()} faqs={viewModel.getFaqs()} />
      )}

      {viewModel.shouldShowBanner() && <DiseaseEncyclopediaBanner banner={viewModel.getBanner()} client={client} />}

      <DiseaseEncyclopediaGuideInfo />

      {viewModel.shouldShowSearchTags() && (
        <DiseaseEncyclopediaSearchTagList searchTags={viewModel.diseaseEncyclopedia?.getSearchTags() ?? []} />
      )}
    </main>
    {viewModel.shouldShowCtaBottomFloatingButton() ? (
      <StickyButton>
        <StickyButtonWrapper>
          <Button
            type='button'
            buttonStyle={'contained'}
            buttonColor='primary'
            size='xl'
            className='w-full'
            onClick={viewModel.handleClickCtaBottomFloatingButton}
          >
            {viewModel.getCtaBottomFloatingButtonText()}
          </Button>
          {viewModel.shouldShowBottomFloatingSocialProof() ? (
            <EventTextArea>
              <HealthMedicalMedicines />
              <EventTextWrapper>
                <EventText>오늘 </EventText>
                <HighlightedEventText>1,379명</HighlightedEventText>
                <EventText>이 비대면 진료로 처방받았어요</EventText>
              </EventTextWrapper>
            </EventTextArea>
          ) : (
            <></>
          )}
        </StickyButtonWrapper>
      </StickyButton>
    ) : (
      <div className='h-[32px]' />
    )}
  </TemplateLayout.Wrapper>
);

const StickyButtonWrapper = styled.div`
  width: 100%;
`;

const EventTextArea = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding-top: 8px;
  gap: 8px;
`;

const EventText = styled.span`
  ${({ theme }) => `
    ${theme.typography.UI15M};
    color: ${theme.color.gray500};
  `};
`;

const HighlightedEventText = styled.span`
  ${({ theme }) => `
    ${theme.typography.UI15SB};
    color: ${theme.color.primary700};
  `};
`;

const EventTextWrapper = styled.span``;

export default DiseaseEncyclopediaDetailView;
